import { API, HOST } from '@/utils/http'

/**
 * Upload a single resource file
 * @param {File} file - The file to upload
 * @param {string|null} featureCode - Optional feature UUID to associate with
 * @param {function} onUploadProgress - Progress callback
 * @returns {Promise<Object>} The uploaded resource data
 */
export async function uploadResource(file, featureCode = null, onUploadProgress = null) {
  const formData = new FormData()
  formData.append('file', file)

  if (featureCode) {
    formData.append('feature_code', featureCode)
  }

  try {
    const response = await API.post(`${HOST}/resources/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onUploadProgress ? (progressEvent) => {
        onUploadProgress(progressEvent)
      } : undefined
    })
    
    return response.data
  } catch (error) {
    console.error('Error uploading resource:', error)
    throw new Error(error.response?.data?.detail || 'Failed to upload file')
  }
}

/**
 * Upload multiple resource files
 * @param {File[]} files - Array of files to upload
 * @param {string|null} featureCode - Optional feature UUID to associate with
 * @returns {Promise<Object>} Upload results with successful and failed uploads
 */
export async function uploadBulkResources(files, featureCode = null) {
  const formData = new FormData()

  files.forEach(file => {
    formData.append('files', file)
  })

  if (featureCode) {
    formData.append('feature_code', featureCode)
  }

  try {
    const response = await API.post(`${HOST}/resources/upload-bulk`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    return response.data
  } catch (error) {
    console.error('Error uploading bulk resources:', error)
    throw new Error(error.response?.data?.detail || 'Failed to upload files')
  }
}

/**
 * Get resources for a feature
 * @param {string} featureCode - The feature UUID
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} List of resources
 */
export async function getResources(featureCode, params = {}) {
  try {
    const response = await API.get(`${HOST}/resources`, {
      params: {
        feature_code: featureCode,
        ...params
      }
    })
    
    return response.data
  } catch (error) {
    console.error('Error fetching resources:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch resources')
  }
}

/**
 * Get a single resource by ID
 * @param {string} resourceId - The resource UUID
 * @returns {Promise<Object>} The resource data
 */
export async function getResource(resourceId) {
  try {
    const response = await API.get(`${HOST}/resources/${resourceId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching resource:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch resource')
  }
}

/**
 * Delete a resource
 * @param {string} resourceId - The resource UUID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteResource(resourceId) {
  try {
    const response = await API.delete(`${HOST}/resources/${resourceId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting resource:', error)
    throw new Error(error.response?.data?.detail || 'Failed to delete resource')
  }
}

/**
 * Copy a resource to another location
 * @param {string} resourceId - The resource UUID to copy
 * @param {Object} data - Copy destination data
 * @returns {Promise<Object>} The copied resource
 */
export async function copyResource(resourceId, data) {
  try {
    const response = await API.post(`${HOST}/resources/${resourceId}/copy`, data)
    return response.data
  } catch (error) {
    console.error('Error copying resource:', error)
    throw new Error(error.response?.data?.detail || 'Failed to copy resource')
  }
}

/**
 * Get resource statistics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Resource statistics
 */
export async function getResourceStats(params = {}) {
  try {
    const response = await API.get(`${HOST}/resources/stats`, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching resource stats:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch resource statistics')
  }
}